import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders} from '@angular/common/http';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { URL_BFM_SESSION_CREATE, BFM_CREDENTIALS, MOCK_STATUS, MOCK_URL_SESSION, MOCK_URL_EXTEND_SESSION, URL_SESSION_RESET, URL_SESSION_RENEW, URL_SESSION_SIGNOUT, URL_SESSION_EXTEND, URL_LOGIN, SESSION_VAR_KEY_SESSION } from '@ibe.config';
import { INDRAStorageService } from './storage.indra.service';
import { INDRACookieService } from './cookie.indra.service';

import { Store } from '@ngrx/store';
import * as fromStore from '../store';
import * as UserActions from '@ibe/store/actions/user.actions';
import * as OptionsBarActions from '@ibe/store/actions/options-bar.actions';
import * as FlightsFilterActions from '@ibe/store/actions/flights-filter.actions';
import { INDRAUserData } from '@ibe.models/user.indra.model.lib';

const RETRY_ON_401 = true;
const DO_NOT_RETRY_ON_401 = false;

@Injectable({
	providedIn: 'root'
})
export class INDRAApiService {

	private creatingSession = false;
	private readonly token: BehaviorSubject<string>;

	hasToken: BehaviorSubject<boolean>;
	ongoingRequest: boolean = false;

	constructor(
		private http: HttpClient,
		private storage: INDRAStorageService,
		private cookieService: INDRACookieService,
		private store: Store<fromStore.State>,
		private storageService: INDRAStorageService
	) {
		this.token = new BehaviorSubject(null);
		this.hasToken = new BehaviorSubject(false);
	}

	private INDRA_getToken(): Observable<string> {

		const promise = new Promise<string>((resolve, reject) => {
			this.token.subscribe(token => {
				if (token) {
					resolve(token);
				} else {
					this.INDRA_createSessionBfm();
				}
			});
		});

		return from(promise);
	}


	private async INDRA_createSessionBfm() {
		if (!this.creatingSession) {

			const tokenStored = this.storage.get(SESSION_VAR_KEY_SESSION);
			if (tokenStored != null) {
				return this.token.next(tokenStored);
			}

			this.creatingSession = true;
			this.ongoingRequest = true;

			const marketCode = sessionStorage.getItem('market');
			if (marketCode) BFM_CREDENTIALS.market = marketCode;
			const languageCode = sessionStorage.getItem('language');
			if (languageCode) BFM_CREDENTIALS.language = languageCode;
			var urlParams = new URLSearchParams(window.location.search);
			const idOperation = urlParams.get('idOperation');
			if (idOperation && idOperation.length) BFM_CREDENTIALS.idOperation = idOperation;
			
			if (MOCK_STATUS.SESSION) {
				this.http.get<any>(MOCK_URL_SESSION).subscribe(response => {
					this.token.next(response['id']);
					this.storage.set(SESSION_VAR_KEY_SESSION, response['id']);
				});
			} else {
        try {
			const response = await this.http.post(URL_BFM_SESSION_CREATE, BFM_CREDENTIALS).toPromise();
   	    	if(response) {
   		    	this.token.next(response['id']);
	  		    this.storage.set(SESSION_VAR_KEY_SESSION, response['id']);

				sessionStorage.setItem('cookieExist', response['cookieExist'] ? 'true' : 'false');

				const currentUrl = window.location.href
				if (!currentUrl.includes('/deeplink') && response['cookieExist']) {
					await this.INDRA_loginCookie();
				}


        		if (response) {
            		this.hasToken.next(true);

            	}
            	this.ongoingRequest = false;
          	}
        }
        catch(err) {

        }

			}
		}
	}

	private executeGet<T> (url: string, resolve, reject, retryOnError = false) {

		this.INDRA_getToken().subscribe(token => {
			const headers = new HttpHeaders({'Authorization': 'Bearer ' + token});
			this.http.get<T>(url, { headers: headers }).subscribe(response => {
				resolve (response);
			}, error => {
				if (error && error.status === 401) {
					this.clearSessionToken();

					if (retryOnError) {
						return this.executeGet(url, resolve, reject, DO_NOT_RETRY_ON_401);
					}

				}

				reject(error);
			});
		});

	}

	public clearSessionToken() {
		this.storage.remove(SESSION_VAR_KEY_SESSION);
		this.token.next(null);
		this.hasToken.next(false);
	}

	public INDRA_get<T> (url: string): Observable<T> {
		const promise = new Promise<T>((resolve, reject) => {

			this.executeGet(url, resolve, reject, RETRY_ON_401);

		});
		return from(promise);
	}

	private executePost<T> (url: string, data: any, resolve, reject, retryOnError = false) {
		this.INDRA_getToken().subscribe(token => {
			const headers = new HttpHeaders({'Authorization': 'Bearer ' + token});
			this.http.post<T>(url, data, { headers: headers }).subscribe(response => {
				resolve(response);
			}, error => {
				if (error && error.status === 401) {
					this.clearSessionToken();

					if (retryOnError) {
						return this.executePost(url, data, resolve, reject, DO_NOT_RETRY_ON_401);
					}
				}

				reject(error);
			});
		});
	}
	
	private executePostWithFullHttpResponse<T> (url: string, data: any, resolve, reject, retryOnError = false) {
		this.INDRA_getToken().subscribe(token => {
			const headers = new HttpHeaders({'Authorization': 'Bearer ' + token});
			this.http.post<T>(url, data, { headers: headers, observe: 'response' }).subscribe(response => {
				resolve(response);
			}, error => {
				if (error && error.status === 401) {
					this.clearSessionToken();

					if (retryOnError) {
						return this.executePostWithFullHttpResponse(url, data, resolve, reject, DO_NOT_RETRY_ON_401);
					}
				}

				reject(error);
			});
		});
	}


	public INDRA_post<T> (url: string, data: any): Observable<T> {
		const promise = new Promise<T>((resolve, reject) => {

			this.executePost(url, data, resolve, reject, RETRY_ON_401);

		});
		return from(promise);
	}

	public INDRA_postFullHttpResponse<T> (url: string, data: any): Observable<T> {
		const promise = new Promise<T>((resolve, reject) => {

			this.executePostWithFullHttpResponse(url, data, resolve, reject, RETRY_ON_401);

		});
		return from(promise);
	}

	private executeDelete<T> (url: string, data: any, resolve, reject, retryOnError = false) {
		this.INDRA_getToken().subscribe(token => {
			const headers = new HttpHeaders({'Authorization': 'Bearer ' + token});
			this.http.request<T>('DELETE', url, { headers: headers, body: data }).subscribe(response => {
				resolve(response);
			}, error => {
				if (error && error.status === 401) {
					this.clearSessionToken();

					if (retryOnError) {
						return this.executeDelete(url, data, resolve, reject, DO_NOT_RETRY_ON_401);
					}
				}

				reject(error);
			});
		});
	}

	public INDRA_delete<T> (url: string, data: any): Observable<T> {
		const promise = new Promise<T>((resolve, reject) => {

			this.executeDelete(url, data, resolve, reject, RETRY_ON_401);

		});
		return from(promise);
	}

	public INDRA_put<T> (url: string, data: any): Observable<T> {
		const promise = new Promise<T>((resolve, reject) => {

			this.executePut(url, data, resolve, reject, RETRY_ON_401);

		});
		return from(promise);
	}

	private executePut<T> (url: string, data: any, resolve, reject, retryOnError = false) {
		this.INDRA_getToken().subscribe(token => {
			const headers = new HttpHeaders({'Authorization': 'Bearer ' + token});
			this.http.put<T>(url, data, { headers: headers }).subscribe(response => {
				resolve(response);
			}, error => {
				if (error && error.status === 401) {
					this.clearSessionToken();

					if (retryOnError) {
						return this.executePut(url, data, resolve, reject, DO_NOT_RETRY_ON_401);
					}
				}

				reject(error);
			});
		});
	}

	public INDRA_createNewSession() {
		this.clearSessionToken();
		this.creatingSession = false;
		this.INDRA_createSessionBfm();
	}

	public INDRA_extendSession() {
		const promise = new Promise((resolve, reject) => {
			this.INDRA_getToken().subscribe(token => {
				const headers = new HttpHeaders({'Authorization': 'Bearer ' + token});
				if (MOCK_STATUS.SESSION) {
					this.http.post<any>(MOCK_URL_EXTEND_SESSION, {}, { headers: headers }).subscribe(response => {
						resolve(response);
					});
				} else {
					this.http.post<any>(URL_SESSION_EXTEND, {}, { headers: headers }).subscribe(response => {
						resolve(response)
					}, error => {
						if (error && error.status === 401) {
							this.clearSessionToken();
						}
						reject(error);
					});
				}
			});	
		});
		return from(promise);	
	}

	public INDRA_retrieveNewToken(): Observable<string> {
		if (!this.ongoingRequest) {
			this.clearSessionToken();
			this.creatingSession = false;
		}

		return this.INDRA_getToken();
	}
	
	/** note: used to reset values/objects stored in the backend ( resets and renews the session itself ) **/
	public resetSessionValues() {
		const promise = new Promise<any>((resolve, reject) => {
			const marketCode = sessionStorage.getItem('market');
			if (marketCode) BFM_CREDENTIALS.market = marketCode;
			const languageCode = sessionStorage.getItem('language');
			if (languageCode) BFM_CREDENTIALS.language = languageCode;
			
			this.INDRA_post<any>(URL_SESSION_RESET, BFM_CREDENTIALS).subscribe(async res => {
				this.token.next(res['id']);
				this.storage.set(SESSION_VAR_KEY_SESSION, res['id']);
				sessionStorage.setItem('cookieExist', res['cookieExist'] ? 'true' : 'false');
				const currentUrl = window.location.href;

				if (!currentUrl.includes('/deeplink') && res['cookieExist']) {
					await this.INDRA_loginCookie();
				}
				
				if (res) {
					this.hasToken.next(true);
				}
				
				this.ongoingRequest = false;
				resolve(res);
			}, error => {
				reject(null);
			});
		});
		
		return from(promise);
	}

	public INDRA_signOutFromSession() {
		return this.INDRA_post<any>(URL_SESSION_SIGNOUT, {});
	}
	
	public INDRA_automaticLogin(loginRequest) {
		return this.INDRA_post<any>(URL_LOGIN, loginRequest);
	}

	async INDRA_loginCookie() {
		try {
			const response = await this.INDRA_loginCookieAPI().toPromise();
		  if (response && response.data && response.errors.length === 0) {
			const { userProfile, userPnrs, translate, userAddress, defaultCurrency } = response.data;
			const userData = {
			  ...userProfile,
			  userPnrs: userPnrs,
			  userAddress: userAddress,
			  translate: translate,
			  userProfile: userProfile,
			  defaultCurrency: defaultCurrency
			};
			this.storage.set('userData', userData);
			const userInfo = { ...userProfile, defaultCurrency };
			this.store.dispatch(new UserActions.PutLoggedInAction(true));
			this.store.dispatch(new UserActions.PutUserInfoAction(userInfo));
			this.store.dispatch(new UserActions.PutUserPNRSAction(userPnrs));
	
			if (this.cookieService.isAward) {
			  this.store.dispatch(new OptionsBarActions.PutPriceUnit('miles'));
			  this.store.dispatch(new FlightsFilterActions.PutBookAwardFlights(true));
			}
		  } else {
			this.INDRA_clearUserInfo();
		  }
		} catch(err) {
		}
	}

	INDRA_loginCookieAPI(): Observable<INDRAUserData> {
		return this.INDRA_post<INDRAUserData>(URL_LOGIN, {});
	}

	INDRA_clearUserInfo() {
		this.store.dispatch(new UserActions.PutLoggedInAction(false));
		this.store.dispatch(new UserActions.PutUserInfoAction(false));
		this.store.dispatch(new UserActions.PutCancelLogIn(false));
		this.store.dispatch(new OptionsBarActions.PutPriceUnit('cash'));
		this.store.dispatch(new FlightsFilterActions.PutBookAwardFlights(false));

		this.storageService.remove('userData');
		this.storageService.remove('user_info');
		this.storageService.remove('prevSelectedLanguage');
	}
}
